import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Copy, RefreshCw, Shield, Eye, EyeOff } from 'lucide-react';
import { Button } from '../ui/Button';
import { Input } from '../ui/Input';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/Card';
import toast from 'react-hot-toast';

export const PasswordGeneratorTool: React.FC = () => {
  const { t } = useTranslation();
  const [password, setPassword] = useState('');
  const [length, setLength] = useState(16);
  const [includeUppercase, setIncludeUppercase] = useState(true);
  const [includeLowercase, setIncludeLowercase] = useState(true);
  const [includeNumbers, setIncludeNumbers] = useState(true);
  const [includeSymbols, setIncludeSymbols] = useState(true);
  const [showPassword, setShowPassword] = useState(true);

  const generatePassword = () => {
    let charset = '';
    if (includeUppercase) charset += 'ABCDEFGHIJKLMNOPQRSTUVWXYZ';
    if (includeLowercase) charset += 'abcdefghijklmnopqrstuvwxyz';
    if (includeNumbers) charset += '0123456789';
    if (includeSymbols) charset += '!@#$%^&*()_+-=[]{}|;:,.<>?';

    if (charset === '') {
      toast.error(t('tools.passwordGenerator.pleaseSelectCharacterType'));
      return;
    }

    let result = '';
    for (let i = 0; i < length; i++) {
      result += charset.charAt(Math.floor(Math.random() * charset.length));
    }
    setPassword(result);
  };

  const copyToClipboard = async (text: string) => {
    try {
      await navigator.clipboard.writeText(text);
      toast.success(t('common.copied'));
    } catch (err) {
      toast.error(t('common.failedToCopy'));
    }
  };

  const getPasswordStrength = (pwd: string) => {
    let score = 0;
    if (pwd.length >= 8) score++;
    if (pwd.length >= 12) score++;
    if (/[a-z]/.test(pwd)) score++;
    if (/[A-Z]/.test(pwd)) score++;
    if (/[0-9]/.test(pwd)) score++;
    if (/[^A-Za-z0-9]/.test(pwd)) score++;

    if (score < 3) return { level: t('tools.passwordGenerator.weak'), color: 'red' };
    if (score < 5) return { level: t('tools.passwordGenerator.medium'), color: 'yellow' };
    return { level: t('tools.passwordGenerator.strong'), color: 'green' };
  };

  React.useEffect(() => {
    generatePassword();
  }, [length, includeUppercase, includeLowercase, includeNumbers, includeSymbols]);

  const strength = password ? getPasswordStrength(password) : null;

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center space-x-2">
            <Shield className="w-5 h-5" />
            <span>{t('tools.passwordGenerator.name')}</span>
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-6">
            {/* Generated Password */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                {t('tools.passwordGenerator.generatedPassword')}
              </label>
              <div className="flex items-center space-x-2">
                <div className="relative flex-1">
                  <input
                    type={showPassword ? 'text' : 'password'}
                    value={password}
                    readOnly
                    className="input font-mono pr-10"
                  />
                  <Button
                    onClick={() => setShowPassword(!showPassword)}
                    variant="ghost"
                    size="sm"
                    className="absolute right-2 top-1/2 transform -translate-y-1/2"
                  >
                    {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </Button>
                </div>
                <Button
                  onClick={generatePassword}
                  variant="outline"
                >
                  <RefreshCw className="w-4 h-4 mr-2" />
                  {t('common.generate')}
                </Button>
                <Button
                  onClick={() => copyToClipboard(password)}
                  disabled={!password}
                >
                  <Copy className="w-4 h-4 mr-2" />
                  {t('common.copy')}
                </Button>
              </div>
              
              {strength && (
                <div className="mt-2 flex items-center space-x-2">
                  <span className="text-sm text-gray-600 dark:text-gray-400">{t('tools.passwordGenerator.strength')}:</span>
                  <span className={`text-sm font-medium ${
                    strength.color === 'green' ? 'text-green-600 dark:text-green-400' :
                    strength.color === 'yellow' ? 'text-yellow-600 dark:text-yellow-400' :
                    'text-red-600 dark:text-red-400'
                  }`}>
                    {strength.level}
                  </span>
                </div>
              )}
            </div>

            {/* Options */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <Input
                  label={t('tools.passwordGenerator.length')}
                  type="number"
                  min="4"
                  max="128"
                  value={length}
                  onChange={(e) => setLength(Math.max(4, Math.min(128, parseInt(e.target.value) || 16)))}
                />
              </div>
              
              <div className="space-y-3">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                  {t('tools.passwordGenerator.characterTypes')}
                </label>
                
                <div className="space-y-2">
                  <label className="flex items-center">
                    <input
                      type="checkbox"
                      checked={includeUppercase}
                      onChange={(e) => setIncludeUppercase(e.target.checked)}
                      className="mr-2"
                    />
                    <span className="text-sm">{t('tools.passwordGenerator.includeUppercase')} (A-Z)</span>
                  </label>
                  
                  <label className="flex items-center">
                    <input
                      type="checkbox"
                      checked={includeLowercase}
                      onChange={(e) => setIncludeLowercase(e.target.checked)}
                      className="mr-2"
                    />
                    <span className="text-sm">{t('tools.passwordGenerator.includeLowercase')} (a-z)</span>
                  </label>
                  
                  <label className="flex items-center">
                    <input
                      type="checkbox"
                      checked={includeNumbers}
                      onChange={(e) => setIncludeNumbers(e.target.checked)}
                      className="mr-2"
                    />
                    <span className="text-sm">{t('tools.passwordGenerator.includeNumbers')} (0-9)</span>
                  </label>
                  
                  <label className="flex items-center">
                    <input
                      type="checkbox"
                      checked={includeSymbols}
                      onChange={(e) => setIncludeSymbols(e.target.checked)}
                      className="mr-2"
                    />
                    <span className="text-sm">{t('tools.passwordGenerator.includeSymbols')} (!@#$...)</span>
                  </label>
                </div>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Password Tips */}
      <Card>
        <CardHeader>
          <CardTitle>{t('tools.passwordGenerator.passwordSecurityTips')}</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-4 text-sm text-gray-600 dark:text-gray-400">
            <div>
              <h4 className="font-medium text-gray-900 dark:text-gray-100 mb-2">{t('tools.passwordGenerator.strongPasswordGuidelines')}</h4>
              <ul className="list-disc list-inside space-y-1">
                <li>{t('tools.passwordGenerator.useAtLeast12Characters')}</li>
                <li>{t('tools.passwordGenerator.includeUpperLowercase')}</li>
                <li>{t('tools.passwordGenerator.addNumbersSpecialChars')}</li>
                <li>{t('tools.passwordGenerator.avoidDictionaryWords')}</li>
                <li>{t('tools.passwordGenerator.useUniquePasswords')}</li>
              </ul>
            </div>
            <div>
              <h4 className="font-medium text-gray-900 dark:text-gray-100 mb-2">{t('tools.passwordGenerator.bestPractices')}</h4>
              <ul className="list-disc list-inside space-y-1">
                <li>{t('tools.passwordGenerator.usePasswordManager')}</li>
                <li>{t('tools.passwordGenerator.enableTwoFactor')}</li>
                <li>{t('tools.passwordGenerator.changeIfCompromised')}</li>
                <li>{t('tools.passwordGenerator.dontSharePasswords')}</li>
                <li>{t('tools.passwordGenerator.regularlyUpdatePasswords')}</li>
              </ul>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};
